import { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, FolderOpen, ImagePlus, Loader2, RotateCcw, X, XCircle } from "lucide-react";
import { api, apiError } from "@/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

const ACCEPTED_EXTENSIONS = ["jpg", "jpeg", "png", "webp"];
const MAX_BYTES = 30 * 1024 * 1024;

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function titleFromFilename(name) {
  const base = name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").trim();
  return base.replace(/\b\w/g, (char) => char.toUpperCase()) || "Untitled";
}

export function BulkUpload({ events, onChanged }) {
  const [eventId, setEventId] = useState("");
  const [price, setPrice] = useState(4.99);
  const [queue, setQueue] = useState([]);
  const [running, setRunning] = useState(false);
  const [autoLabel, setAutoLabel] = useState(true);
  const queueRef = useRef([]);

  useEffect(() => { queueRef.current = queue; }, [queue]);
  useEffect(() => { if (!eventId && events?.length) setEventId(events[0].id); }, [events]);

  const counts = useMemo(() => ({
    total: queue.length,
    done: queue.filter((item) => item.status === "done").length,
    failed: queue.filter((item) => item.status === "failed").length,
    queued: queue.filter((item) => item.status === "queued").length,
  }), [queue]);

  const overallPercent = counts.total ? Math.round((counts.done / counts.total) * 100) : 0;

  function updateItem(id, patch) {
    setQueue((current) => current.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }

  function addFiles(fileList) {
    const files = Array.from(fileList || []);
    const accepted = [];
    const rejected = [];
    files.forEach((file) => {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      if (!ACCEPTED_EXTENSIONS.includes(ext)) rejected.push(`${file.name} (unsupported type)`);
      else if (file.size > MAX_BYTES) rejected.push(`${file.name} (over 30MB)`);
      else accepted.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        name: file.name,
        title: titleFromFilename(file.name),
        size: file.size,
        status: "queued",
        progress: 0,
        error: "",
      });
    });
    if (accepted.length) setQueue((current) => [...current, ...accepted]);
    if (rejected.length) toast.error(`Skipped ${rejected.length} file(s): ${rejected.slice(0, 3).join(", ")}${rejected.length > 3 ? "…" : ""}`);
  }

  async function uploadOne(id) {
    const item = queueRef.current.find((entry) => entry.id === id);
    if (!item || item.status === "done") return;
    updateItem(id, { status: "uploading", progress: 0, error: "" });
    try {
      const data = new FormData();
      data.append("file", item.file);
      data.append("title", item.title);
      data.append("price", String(price));
      data.append("vehicle", "");
      data.append("exif", "{}");
      data.append("auto_label", autoLabel ? "true" : "false");
      const response = await api.post(`/admin/events/${eventId}/photos`, data, {
        onUploadProgress: (event) => {
          if (event.total) updateItem(id, { progress: Math.round((event.loaded / event.total) * 100) });
        },
      });
      updateItem(id, { status: "done", progress: 100, ai_labeled: Boolean(response.data.ai_labeled) });
    } catch (error) {
      updateItem(id, { status: "failed", error: apiError(error) });
    }
  }

  async function run(ids) {
    if (!ids.length || !eventId) return;
    setRunning(true);
    const pending = [...ids];
    async function worker() {
      while (pending.length) {
        const id = pending.shift();
        await uploadOne(id);
      }
    }
    await Promise.all([worker(), worker()]);
    setRunning(false);
    onChanged?.();
    const failed = queueRef.current.filter((item) => item.status === "failed").length;
    if (failed) toast.error(`${failed} file(s) failed. Use retry to try again.`);
    else toast.success("Bulk upload complete");
  }

  function startAll() {
    run(queueRef.current.filter((item) => item.status === "queued" || item.status === "failed").map((item) => item.id));
  }

  function retryFailed() {
    run(queueRef.current.filter((item) => item.status === "failed").map((item) => item.id));
  }

  function removeItem(id) {
    setQueue((current) => current.filter((item) => item.id !== id));
  }

  function clearDone() {
    setQueue((current) => current.filter((item) => item.status !== "done"));
  }

  return (
    <section data-testid="bulk-upload-section" className="mt-12 border border-white/10 bg-carbon p-6">
      <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
        <div>
          <h2 className="font-display text-3xl text-white">Bulk upload</h2>
          <p className="mt-3 max-w-2xl text-sm leading-7 text-zinc-500">Drop an entire event folder or pick multiple photos. Each file is stored as a protected original with an auto-generated watermarked preview. Failed files stay in the queue for retry.</p>
        </div>
        <div className="flex gap-2">
          <label data-testid="bulk-folder-picker" className="inline-flex cursor-pointer items-center gap-2 border border-white/15 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-300 transition-colors hover:border-gold/50 hover:text-gold">
            <FolderOpen className="h-4 w-4" /> Choose folder
            <input data-testid="bulk-folder-input" type="file" multiple webkitdirectory="" className="hidden" onChange={(event) => { addFiles(event.target.files); event.target.value = ""; }} />
          </label>
          <label data-testid="bulk-files-picker" className="inline-flex cursor-pointer items-center gap-2 border border-white/15 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-300 transition-colors hover:border-gold/50 hover:text-gold">
            <ImagePlus className="h-4 w-4" /> Choose photos
            <input data-testid="bulk-files-input" type="file" multiple accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={(event) => { addFiles(event.target.files); event.target.value = ""; }} />
          </label>
        </div>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-[1fr_140px_auto_auto] sm:items-center">
        <Select value={eventId} onValueChange={setEventId}>
          <SelectTrigger data-testid="bulk-event-select" className="border-white/10 bg-white/[0.03]"><SelectValue placeholder="Choose event" /></SelectTrigger>
          <SelectContent>{events.map((eventItem) => <SelectItem key={eventItem.id} value={eventItem.id}>{eventItem.title}</SelectItem>)}</SelectContent>
        </Select>
        <Input data-testid="bulk-price-input" type="number" min="1" step="0.01" value={price} onChange={(event) => setPrice(event.target.value)} className="border-white/10 bg-white/[0.03]" title="Price per photo" />
        <Button data-testid="bulk-start-button" disabled={running || !counts.queued && !counts.failed} onClick={startAll} className="rounded-none bg-gold font-mono text-xs uppercase tracking-[0.2em] text-onyx hover:bg-titanium">
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Start upload
        </Button>
        <Button data-testid="bulk-retry-failed-button" disabled={running || !counts.failed} onClick={retryFailed} variant="outline" className="rounded-none border-gold/40 font-mono text-xs uppercase tracking-[0.2em] text-gold hover:bg-gold hover:text-onyx">
          <RotateCcw className="h-4 w-4" /> Retry failed ({counts.failed})
        </Button>
      </div>

      <label className="mt-4 flex items-center gap-3 text-sm text-zinc-400">
        <input data-testid="bulk-auto-label-toggle" type="checkbox" checked={autoLabel} onChange={(event) => setAutoLabel(event.target.checked)} />
        AI label each photo on upload (cinematic title, vehicle, search tags)
      </label>

      {queue.length > 0 && (
        <div className="mt-6">
          <div className="mb-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
            <span data-testid="bulk-overall-counts">{counts.done}/{counts.total} uploaded · {counts.failed} failed · {counts.queued} queued</span>
            <button data-testid="bulk-clear-done-button" onClick={clearDone} className="text-zinc-500 transition-colors hover:text-white">Clear completed</button>
          </div>
          <div data-testid="bulk-overall-progress" className="h-1 w-full bg-white/10">
            <div className="h-full bg-gold transition-all duration-500" style={{ width: `${overallPercent}%` }} />
          </div>
          <ul className="mt-4 divide-y divide-white/10 border border-white/10">
            {queue.map((item) => (
              <li key={item.id} data-testid={`bulk-item-${item.id}`} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-lux text-sm text-white">{item.name}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">{formatBytes(item.size)} → {item.title}{item.ai_labeled && <span className="ml-2 border border-gold/30 px-1.5 py-0.5 font-mono text-[9px] tracking-[0.14em] text-gold">AI labeled</span>}</p>
                  {item.status === "uploading" && (
                    <div data-testid={`bulk-item-progress-${item.id}`} className="mt-2 h-1 w-full max-w-xs bg-white/10">
                      <div className="h-full bg-gold transition-all duration-300" style={{ width: `${item.progress}%` }} />
                    </div>
                  )}
                  {item.error && <p data-testid={`bulk-item-error-${item.id}`} className="mt-2 text-xs text-red-300">{item.error}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <span data-testid={`bulk-item-status-${item.id}`} className={`inline-flex items-center gap-1 border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] ${
                    item.status === "done" ? "border-green-500/40 text-green-300"
                    : item.status === "failed" ? "border-red-500/40 text-red-300"
                    : item.status === "uploading" ? "border-gold/40 text-gold"
                    : "border-white/15 text-zinc-500"
                  }`}>
                    {item.status === "done" && <CheckCircle2 className="h-3 w-3" />}
                    {item.status === "failed" && <XCircle className="h-3 w-3" />}
                    {item.status === "uploading" && <Loader2 className="h-3 w-3 animate-spin" />}
                    {item.status}
                  </span>
                  {item.status === "failed" && (
                    <button data-testid={`bulk-item-retry-${item.id}`} disabled={running} onClick={() => run([item.id])} className="border border-gold/40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-gold transition-colors hover:bg-gold hover:text-onyx">
                      Retry
                    </button>
                  )}
                  {item.status !== "uploading" && (
                    <button data-testid={`bulk-item-remove-${item.id}`} disabled={running} onClick={() => removeItem(item.id)} className="p-2 text-zinc-600 transition-colors hover:text-white" aria-label={`Remove ${item.name}`}>
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
